import React, { useEffect } from "react";

import { isAxiosError } from "axios";
import { useForm } from "react-hook-form";

import { ConfirmationModal } from "src/base-components/ConfirmationModal";
import { ErrorHint } from "src/base-components/ErrorHint";
import { Input } from "src/base-components/Input";
import { DEFAULT_STRING_LENGTH } from "src/base-components/schemaKit/validators";
import { usePublishCaseTemplateVersion } from "src/cases/queries/templates";
import { JsonApiResourceObjectCaseTemplateVersionAttributesBaseModel as CaseTemplateVersion } from "src/clients/case-management";
import { toastActions } from "src/design-system/Toast/utils";
import { logger } from "src/utils/logger";
import { isPreconditionError } from "src/utils/predicates";
import { getApiErrorDetail } from "src/utils/stringUtils";

type PublishTemplateVersionInput = {
  name: string;
};

type PublishTemplateVersionModalProps = {
  open: boolean;
  onClose: () => void;
  templateId: string;
  version: CaseTemplateVersion | null;
  onPublishSuccess?: () => void;
};

export const PublishTemplateVersionModal: React.FC<
  PublishTemplateVersionModalProps
> = ({ open, onClose, templateId, version, onPublishSuccess }) => {
  const { mutateAsync: updateVersion } = usePublishCaseTemplateVersion();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<PublishTemplateVersionInput>({
    mode: "onBlur",
  });

  useEffect(() => {
    if (open && version) {
      reset({
        name: version.attributes.version_name,
      });
    }
  }, [reset, open, version]);

  const onConfirm = handleSubmit(async (input: PublishTemplateVersionInput) => {
    if (!version) return;

    try {
      await updateVersion({
        templateId,
        versionId: version.id,
        etag: version.attributes.etag,
        name: input.name,
      });

      onPublishSuccess?.();
      onClose();
    } catch (error) {
      logger.error("Failed to publish version", error);

      if (isPreconditionError(error)) {
        toastActions.failure({
          title: "Failed to publish version",
          description:
            "Someone made changes while you were editing. Please reload and try again",
        });
        return;
      }

      if (isAxiosError(error)) {
        toastActions.failure({
          title: "Failed to publish version",
          description: getApiErrorDetail(error, "An unknown error occurred"),
        });
        return;
      }

      toastActions.failure({
        title: "Failed to publish version",
        description: "An unknown error occurred",
      });
    }
  });

  return (
    <ConfirmationModal
      confirmationButtonText="Publish version"
      description={
        <>
          <p>You are about to publish a new version of this Case template</p>
          <p>
            You can still update the Decision flows connected to this template
            version
          </p>
        </>
      }
      open={open}
      size="sm"
      title="Publish version"
      onClose={onClose}
      onConfirm={onConfirm}
    >
      <div className="flex flex-col gap-y-2">
        <div className="mt-3 flex flex-col font-inter-normal-12px text-gray-600">
          {`Publish draft <${version?.attributes.version_name || ""}> as`}
        </div>
        <Input
          defaultValue={version?.attributes.version_name}
          errored={Boolean(errors.name)}
          placeholder="Version name"
          fullWidth
          {...register("name", {
            required: "Version name is required",
            maxLength: {
              value: DEFAULT_STRING_LENGTH,
              message: `Version name must be less than ${DEFAULT_STRING_LENGTH} characters`,
            },
          })}
        />
        {errors.name?.message && <ErrorHint>{errors.name.message}</ErrorHint>}
      </div>
    </ConfirmationModal>
  );
};
